<template>
  <li class="ak-tree-item" :class="{ 'ak-tree-item__hide': dragging }">
    <div
      v-if="allowDrop"
      :class="{
        'ak-tree-item__drop-target': true,
        'ak-tree-item__drop-target-active':
          dragHTML !== '' && hoverTarget === 'parent',
      }"
      @dragenter.stop.prevent="dropTargetEnter('parent')"
      @dragover.stop.prevent
      @dragleave.stop.prevent="dropTargetLeave"
      @drop="dropTarget"
      v-html="hoverTarget === 'parent' ? dragHTML : ''"
    ></div>
    <div v-else class="ak-tree-item__empty-drop"></div>
    <div
      :draggable="true"
      @dragstart.stop="startDragItem"
      @dragend.stop="endDragItem"
    >
      <div class="ak-tree-item__content" :class="itemClasses">
        <toggle-children v-model="showChildren" :show="showChildrenToggle" />
        <div class="ak-tree-item__content-display">
          <slot
            name="default"
            :item="data"
            :open="showChildren"
            :is-tree="isTree"
            :children="children"
          />
        </div>
      </div>
    </div>
    <ul v-if="showChildren" :class="childTreeClasses">
      <template v-for="(child, childIndex) in children" :key="childIndex">
        <ak-drag-and-drop-tree-item
          v-model:children="child.children"
          :data="child.data"
          :parent="{ data, children }"
          :index="childIndex"
          :current-depth="currentDepth + 1"
          @update-children="updateChildren"
        >
          <template #default="slotProps">
            <slot
              name="default"
              :item="slotProps.item"
              :open="slotProps.open"
              :is-tree="slotProps.isTree"
              :children="slotProps.children"
            />
          </template>
          <template #empty>
            <slot name="empty">Empty</slot>
          </template>
        </ak-drag-and-drop-tree-item>
      </template>
      <li v-if="showEmptyChild">
        <div class="ak-tree-item__content">
          <toggle-children :show="false" />
          <div
            v-if="allowEmptyDrop"
            :class="{
              'ak-tree-item__drop-target': true,
              'ak-tree-item__drop-target-active':
                dragHTML !== '' && hoverTarget === 'empty',
            }"
            @dragenter.stop.prevent="dropTargetEnter('empty')"
            @dragover.stop.prevent
            @dragleave.stop.prevent="dropTargetLeave"
            @drop="dropTargetEmpty"
          >
            <div
              v-if="dragHTML !== '' && hoverTarget === 'empty'"
              v-html="dragHTML"
            />
            <slot v-else name="empty">Empty</slot>
          </div>
          <div v-else>
            <slot name="empty">Empty</slot>
          </div>
        </div>
      </li>
    </ul>
  </li>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  inject,
  nextTick,
  watch,
  onMounted,
} from 'vue'
import ToggleChildren from './ToggleChildren.vue'
export default defineComponent({
  name: 'AkDragAndDropTreeItem',
  components: {
    [ToggleChildren.name]: ToggleChildren,
  },
  props: {
    /**
     * @type {Object}
     * @description The data for the tree item
     */
    data: {
      type: Object,
      default: () => ({}),
    },
    /**
     * @type {Array}
     * @description The children for the sub tree
     */
    children: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Object}
     * @description The parent on the tree item
     */
    parent: {
      type: Object,
      default: () => ({}),
    },
    /**
     * @type {Number}
     * @description What index is the tree item at in the list
     */
    index: {
      type: Number,
      default: 0,
    },
    /**
     * @type {Number}
     * @description What depth into the tree/sub tree are we at
     */
    currentDepth: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update-children', 'update:children'],
  setup(props, { emit }) {
    const showChildren = ref(false)
    const dragHTML = ref('')
    const dragging = ref(false)
    const hoverTarget = ref(null)

    /**
     * @description Inject all the functions we need from ak-drag-and-drop-tree
     */
    const showEmpty = inject('showEmpty')
    const setDragTarget = inject('setDragTarget')
    const setDropTarget = inject('setDropTarget')
    const moveItem = inject('moveItem')
    const getDragHTML = inject('getDragHTML')
    const getDragTarget = inject('getDragTarget')
    const maxDepth = inject('maxDepth')
    const maxItemDepth = inject('maxItemDepth')
    const getIsDragTargetTree = inject('getIsDragTargetTree')
    const getDragTargetDepth = inject('getDragTargetDepth')
    const updateOriginalLocation = inject('updateOriginalLocation')
    const treeOpened = inject('treeOpened')
    const treeClosed = inject('treeClosed')
    const checkTree = inject('isTree')
    const isTree = checkTree(props.data)
    const isOpen = inject('isOpen')
    const extraItemClass = inject('extraItemClass')

    watch(showChildren, (value) => {
      if (value) {
        treeOpened(props.data)
      } else {
        treeClosed(props.data)
      }
    })

    /**
     * @type {Object}
     * @description The css classes needed to hide and show the sub tree
     */
    const childTreeClasses = computed(() => {
      return {
        'ak-tree-item__child-tree': true,
        'ak-tree-item__child-tree-hidden': !showChildren.value,
        bl: true,
      }
    })

    /**
     * @type {Object}
     * @description css classes need for possible drop locations
     */
    const dropTargetClasses = computed(() => {
      return {
        'ak-tree-item__drop-target': true,
        'ak-tree-item__drop-target-active': dragHTML.value !== '',
      }
    })

    /**
     * @type {Boolean}
     * @description Show or hide the open/close sub tree buttons
     */
    const showChildrenToggle = computed(() => {
      if (!checkTree(props.data)) return false
      if (showEmpty) return true
      if (props.children.length > 0) return true
      return false
    })

    /**
     * @type {Boolean}
     * @description Is dropping allowed in the item
     */
    const allowDrop = computed(() => {
      const max = maxItemDepth()
      if (max === -1) return true
      if (max >= props.currentDepth) {
        return true
      }
      return false
    })

    /**
     * @type {Boolean}
     * @description Is dropping allowed in the empty items location
     */
    const allowEmptyDrop = computed(() => {
      const max = maxItemDepth()
      const current = props.currentDepth + 1
      if (max === -1) return true
      if (max >= current) {
        return true
      }
      return false
    })

    /**
     * @type {Boolean}
     * @description Should the empty location be shown
     */
    const showEmptyChild = computed(() => {
      if (props.children.length > 0) return false
      if (!showEmpty) return false
      return true
    })

    const itemClasses = computed(() => {
      return extraItemClass(props.data)
    })

    const startDragItem = (event) => {
      showChildren.value = false
      setDragTarget(
        { data: props.data, children: props.children },
        event.target.innerHTML,
        removeFromParent
      )
      dragging.value = true
      const node = event.target.cloneNode(true)
      node.classList.add('ak-tree-item__dragging')
      node.setAttribute('id', 'current-drag-item')
      node.style.width = event.target.offsetWidth + 'px'
      window.document.body.appendChild(node)
      event.dataTransfer.setDragImage(node, 0, 0)
    }

    const endDragItem = () => {
      window.document.getElementById('current-drag-item').remove()
      dragging.value = false
    }

    const dropTargetEnter = (target) => {
      hoverTarget.value = target
      setDropTarget(props.parent, props.index)
      dragHTML.value = getDragHTML()
    }

    const dropTargetLeave = () => {
      nextTick(() => {
        hoverTarget.value = null
        dragHTML.value = ''
        setDropTarget(null)
      })
    }

    const removeFromParent = () => {
      const index = props.index
      const temp = [...props.parent.children]
      temp.splice(index, 1)
      emit('update-children', temp)
    }

    /**
     * @description User drops currently dragged item some where in a tree or sub tree that isn't an empty location
     */
    const dropTarget = () => {
      let canDrop = true
      dragHTML.value = ''
      hoverTarget.value = null
      // Do a quick check for sub tree depth to allow for separate depths
      if (getIsDragTargetTree()) {
        const fullDepth = props.currentDepth + getDragTargetDepth()
        if (fullDepth > maxDepth()) {
          canDrop = false
        }
      }
      if (canDrop) {
        const temp = [...props.parent.children]
        const dragTarget = getDragTarget()
        let activeIndex = props.index
        const found = temp.find((i) => {
          return i.data === dragTarget.data
        })
        if (found) {
          const sameLevelIndex = temp.indexOf(found)
          temp.splice(sameLevelIndex, 1)
          if (sameLevelIndex < activeIndex) {
            activeIndex--
          }
          activeIndex = Math.max(activeIndex, 0)
        }
        temp.splice(activeIndex, 0, dragTarget)
        emit('update-children', temp)
        if (!found) {
          updateOriginalLocation()
        }
        moveItem(props.data)
      }
    }

    /**
     * @description User drops currently dragged in into an empty location
     */
    const dropTargetEmpty = () => {
      let canDrop = true
      dragHTML.value = ''
      hoverTarget.value = null
      // Do a quick check for sub tree depth to allow for separate depths
      if (getIsDragTargetTree()) {
        const fullDepth = props.currentDepth + getDragTargetDepth() + 1
        if (fullDepth > maxDepth()) {
          canDrop = false
        }
      }
      if (canDrop) {
        const newChildren = [getDragTarget()]
        const found = props.parent.children.find((i) => {
          return i.data === props.data
        })
        const foundIndex = props.parent.children.indexOf(found)
        const temp = [...props.parent.children]
        temp[foundIndex].children = newChildren
        emit('update-children', temp)
        updateOriginalLocation()
        moveItem()
        nextTick(() => {
          showChildren.value = true
        })
      }
    }

    /**
     * @param {Array} array
     * @description update the children array
     */
    const updateChildren = (array) => {
      emit('update:children', array)
    }

    onMounted(() => {
      showChildren.value = isOpen(props.data)
    })

    return {
      showChildren,
      childTreeClasses,
      showChildrenToggle,
      showEmptyChild,
      dragHTML,
      startDragItem,
      endDragItem,
      dropTargetEnter,
      dropTargetLeave,
      dropTarget,
      dropTargetClasses,
      allowDrop,
      allowEmptyDrop,
      dragging,
      updateChildren,
      hoverTarget,
      dropTargetEmpty,
      isTree,
      itemClasses,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-tree-item {
  &__hide {
    display: none;
  }
  &__dragging {
    border-radius: 5px;
    border: 1px solid $color-primary;
    position: absolute;
    top: -100px;
  }
  &__content {
    display: flex;
    align-items: center;
    align-content: center;
    padding: $spacing-tiny $spacing-tiny / 2;
    border-radius: 5px;
    line-height: 1.3;
    cursor: pointer;
    &:hover {
      background-color: $color-grey-lighter;
    }
    &-display {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }
  &__empty-drop {
    min-height: $spacing-tiny;
    margin-top: $spacing-tiny;
  }
  &__drop-target {
    min-height: $spacing-tiny / 2;
    border-radius: 5px;
    margin-top: $spacing-tiny / 2;
    margin-bottom: $spacing-tiny / 2;
    .ak-tree-item__content,
    div {
      pointer-events: none;
    }
    &-active {
      border: 1px solid $color-primary;
      background-color: $color-primary-light;
      .ak-tree-item__content {
        padding: $spacing-tiny $spacing-tiny / 2 !important;
      }
    }
  }
  &__child-tree {
    margin-left: $spacing-small;
  }
}
</style>
